import type { PendingEntregaPush, PendingEntregaPushPayload } from './models/EntregaCatalog.ts';
import type { SyncPushStoredMediaRef } from '@core/domain/sync/SyncPushResult.ts';
import { participanteDeliveryLabel, participanteRequiresDeliveryMedia } from './entregaDeliveryReadiness.ts';

export type EntregaSyncMediaIssue = {
  entregaPublicId: string;
  message: string;
};

const PARTICIPANTE_CEDULA_MEDIA_KEYS = [
  'cedulaBeneficiarioFrontMediaId',
  'cedulaBeneficiarioBackMediaId',
  'cedulaAutorizadoFrontMediaId',
  'cedulaAutorizadoBackMediaId',
  'documentoAutorizacionMediaId',
] as const;

type EntregaParticipantePushRow = PendingEntregaPush['participantes'][number];

function participanteHasCedulaMediaIds(participante: EntregaParticipantePushRow): boolean {
  return PARTICIPANTE_CEDULA_MEDIA_KEYS.some((key) => participante[key]?.trim());
}

/** Accepted entrega child rows with local media but no matching server ack — purge intentionally skipped. */
export function countEntregaMediaPurgeSkipped(
  pending: PendingEntregaPush[],
  acceptedPublicIds: readonly string[],
  storedMedia: readonly SyncPushStoredMediaRef[],
): number {
  const accepted = new Set(acceptedPublicIds);
  const storedById = new Map(storedMedia.map((row) => [row.publicId, row]));
  let skipped = 0;

  for (const entrega of pending) {
    if (!accepted.has(entrega.publicId)) continue;
    for (const participante of entrega.participantes) {
      const stored = storedById.get(participante.publicId);
      if (participanteRequiresDeliveryMedia(participante)) {
        if (participante.firmaMediaId && !stored?.firmaRef) skipped += 1;
        if (participante.fotoMediaId && !stored?.fotoRef) skipped += 1;
      }
      if (participanteHasCedulaMediaIds(participante) && !stored) {
        for (const key of PARTICIPANTE_CEDULA_MEDIA_KEYS) {
          if (participante[key]?.trim()) skipped += 1;
        }
      }
    }
    for (const remision of entrega.remisiones) {
      const stored = storedById.get(remision.publicId);
      // Inclusion Social ack: firmaEntregaBase64 maps to storedMedia.fotoRef (second signature slot).
      if (remision.firmaRecibeMediaId && !stored?.firmaRef) skipped += 1;
      if (remision.firmaEntregaMediaId && !stored?.fotoRef) skipped += 1;
    }
  }
  return skipped;
}

/** Firma/foto base64 optional on push — never blocks sync. Usuario required; PDF not sent. */
export function entregaSyncPushPayloadIssues(
  _pending: PendingEntregaPush[],
  payload: PendingEntregaPushPayload[],
): EntregaSyncMediaIssue[] {
  const issues: EntregaSyncMediaIssue[] = [];
  for (const row of payload) {
    if (!row.usuarioPublicId?.trim()) {
      issues.push({
        entregaPublicId: row.publicId,
        message: 'Falta el id del usuario en la sesión. Cierre sesión e inicie de nuevo.',
      });
    }
  }
  return issues;
}

/** Server accepted entrega but did not confirm stored media refs for acudiente docs. */
export function entregaSyncMediaAckIssues(
  pending: PendingEntregaPush[],
  acceptedPublicIds: readonly string[],
  storedMedia: readonly SyncPushStoredMediaRef[],
): EntregaSyncMediaIssue[] {
  const accepted = new Set(acceptedPublicIds);
  const storedById = new Map(storedMedia.map((row) => [row.publicId, row]));
  const issues: EntregaSyncMediaIssue[] = [];

  for (const entrega of pending) {
    if (!accepted.has(entrega.publicId)) continue;
    const parts: string[] = [];
    for (const participante of entrega.participantes) {
      const stored = storedById.get(participante.publicId);
      const label = participanteDeliveryLabel(participante);
      if (participanteHasCedulaMediaIds(participante) && !stored) {
        parts.push(`documentos de acudiente de ${label}`);
      }
    }
    if (parts.length > 0) {
      issues.push({
        entregaPublicId: entrega.publicId,
        message: `El servidor no confirmó el almacenamiento de: ${parts.join(', ')}.`,
      });
    }
  }
  return issues;
}
